import React from 'react';
import { Box, Typography, IconButton, Chip, Tooltip } from '@mui/material';
import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  IndeterminateCheckBox as IndeterminateCheckBoxIcon,
  Folder as FolderIcon,
  Description as FileIcon
} from '@mui/icons-material';

/**
 * Tree node renderer component
 *
 * Supports multiple selection modes and custom rendering
 *
 * Selection Modes:
 * - "none" - No selection UI (view-only)
 * - "checkbox" - Checkbox selection with indeterminate state
 * - "view-only" - Show structure but no interaction
 */
const TreeNode = ({
  node,
  checked = false,
  indeterminate = false,
  selectionMode = 'checkbox',
  onToggle,
  renderLabel,
  renderIcon,
  renderActions,
  showNodeActions = false,
  disabled = false
}) => {
  // Default icon based on node type
  const defaultIcon = node.children && node.children.length > 0
    ? <FolderIcon />
    : <FileIcon />;

  // Render checkbox/radio for selection
  const renderCheckbox = () => {
    if (selectionMode === 'none' || selectionMode === 'view-only') return null;

    return (
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onToggle?.(node);
        }}
        disabled={disabled}
        sx={{ mr: 1 }}
      >
        {indeterminate ? (
          <IndeterminateCheckBoxIcon color="primary" />
        ) : checked ? (
          <CheckBoxIcon color="primary" />
        ) : (
          <CheckBoxOutlineBlankIcon />
        )}
      </IconButton>
    );
  };

  // Render node icon
  const nodeIcon = renderIcon ? renderIcon(node) : defaultIcon;

  // Render node label
  const nodeLabel = renderLabel ? (
    renderLabel(node)
  ) : (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: checked ? 600 : 400 }}>
        {node.name || node.label}
      </Typography>
      {node.path && (
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {node.path}
        </Typography>
      )}
      {node.description && (
        <Typography variant="caption" color="text.secondary" display="block">
          {node.description}
        </Typography>
      )}
    </Box>
  );

  // Render child count
  const childCount = node.children?.length || 0;

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      py={0.5}
      width="100%"
    >
      <Box display="flex" alignItems="center" flex={1}>
        {renderCheckbox()}

        {nodeIcon && (
          <Box sx={{ mr: 1, color: 'action.active', display: 'flex' }}>
            {nodeIcon}
          </Box>
        )}

        <Box flex={1}>
          {nodeLabel}
        </Box>

        {childCount > 0 && (
          <Tooltip title={`${childCount} child${childCount !== 1 ? 'ren' : ''}`}>
            <Chip
              label={childCount}
              size="small"
              sx={{ ml: 1 }}
            />
          </Tooltip>
        )}
      </Box>

      {showNodeActions && renderActions && (
        <Box sx={{ ml: 1 }}>
          {renderActions(node)}
        </Box>
      )}
    </Box>
  );
};

export default TreeNode;
